import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { CheckCircle, Home, BookOpen, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const { language } = useLanguage();
  const { clearCart } = useCart();
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const sessionId = searchParams.get('session_id');

  const content = {
    en: {
      title: "Payment Successful!",
      subtitle: "Thank you for your purchase",
      description: "Your course enrollment has been confirmed. You will receive a confirmation email shortly with access details.",
      backHome: "Back to Home",
      viewCourses: "Browse More Courses",
      orderRef: "Order Reference",
      loading: "Verifying payment..."
    },
    ar: {
      title: "تم الدفع بنجاح!",
      subtitle: "شكراً لشرائك",
      description: "تم تأكيد تسجيلك في الدورة. ستتلقى بريداً إلكترونياً للتأكيد قريباً مع تفاصيل الوصول.",
      backHome: "العودة للرئيسية",
      viewCourses: "تصفح المزيد من الدورات",
      orderRef: "رقم الطلب",
      loading: "جارٍ التحقق من الدفع..."
    }
  };

  const t = content[language];

  useEffect(() => {
    // Clear the cart after successful payment
    clearCart();

    // Verify payment status if session_id is present
    const verifyPayment = async () => {
      if (sessionId) {
        try {
          const response = await axios.get(`${BACKEND_URL}/api/payments/checkout/status/${sessionId}`);
          setPaymentStatus(response.data);
        } catch (error) {
          console.error('Error verifying payment:', error);
        }
      }
      setLoading(false);
    };

    verifyPayment();
  }, [sessionId, clearCart]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-teal-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">{t.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 flex items-center justify-center p-4">
        <div className="max-w-lg w-full">
          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 text-center">
            {/* Success Icon */}
            <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" role="img" aria-label="Success">
              <CheckCircle className="w-16 h-16 text-green-500" />
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            {t.title}
          </h1>
          <p className="text-xl text-teal-600 font-semibold mb-6">
            {t.subtitle}
          </p>

          {/* Description */}
          <p className="text-gray-600 mb-8 leading-relaxed">
            {t.description}
          </p>

          {/* Order Reference */}
          {sessionId && (
            <div className="bg-gray-50 rounded-xl p-4 mb-8">
              <p className="text-sm text-gray-500 mb-1">{t.orderRef}</p>
              <p className="font-mono text-sm text-gray-700 break-all">{sessionId}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-4">
            <Link to="/" className="block">
              <Button className="w-full bg-teal-600 hover:bg-teal-700 text-white py-6 text-lg rounded-xl">
                <Home className={`w-5 h-5 ${language === 'ar' ? 'ml-2' : 'mr-2'}`} />
                {t.backHome}
              </Button>
            </Link>
            <Link to="/#courses" className="block">
              <Button variant="outline" className="w-full py-6 text-lg rounded-xl">
                <BookOpen className={`w-5 h-5 ${language === 'ar' ? 'ml-2' : 'mr-2'}`} />
                {t.viewCourses}
              </Button>
            </Link>
          </div>
        </div>

        {/* Support Note */}
        <p className="text-center text-gray-500 text-sm mt-6">
          {language === 'en' 
            ? 'Need help? Contact us via WhatsApp' 
            : 'تحتاج مساعدة؟ تواصل معنا عبر واتساب'}
        </p>
      </div>
    </div>
    </>
  );
};

export default PaymentSuccess;
